import type { FC } from 'react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Container, Stack, SvgIcon, Typography } from '@mui/material';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { paths } from 'src/paths';
import { projects, projectCategories, type Project } from 'src/data/projects';

type Filter = 'All' | Project['category'];

const FILTERS: Filter[] = ['All', ...projectCategories];

export const ProjectsPage: FC = () => {
  usePageView();
  const [activeFilter, setActiveFilter] = useState<Filter>('All');

  const filteredProjects =
    activeFilter === 'All'
      ? projects
      : projects.filter((p) => p.category === activeFilter);

  // CollectionPage + ItemList schema for the entire portfolio
  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': 'https://mysticmediafilm.com/projects#collection',
    name: 'Mystic Media Film: Recent Work',
    url: 'https://mysticmediafilm.com/projects',
    description:
      'Recent work from Mystic Media Film: cinematic commercials, brand films, websites, sports content, and creative production for Houston-area clients.',
    isPartOf: { '@id': 'https://mysticmediafilm.com/#website' },
    about: { '@id': 'https://mysticmediafilm.com/#organization' },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: projects.length,
      itemListElement: projects.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': p.category === 'Websites' ? 'WebPage' : 'VideoObject',
          name: p.title,
          description: p.description,
          url: p.src,
          ...(p.image ? { thumbnailUrl: `https://mysticmediafilm.com${p.image}` } : {}),
          provider: { '@id': 'https://mysticmediafilm.com/#organization' }
        }
      }))
    }
  };

  return (
    <>
      <Seo
        title="Recent Work | Mystic Media Film"
        description="Recent work from Mystic Media Film. Cinematic commercials, brand films, websites, sports content, and full-scope production for ambitious Houston-area brands."
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      </Helmet>

      <main style={{ background: '#ffffff' }}>
        {/* Hero */}
        <Box
          sx={{
            background: '#ffffff',
            pt: { xs: 14, md: 18 },
            pb: { xs: 6, md: 8 }
          }}
        >
          <Container maxWidth="lg">
            <Stack alignItems="center" spacing={{ xs: 3, md: 4 }} sx={{ textAlign: 'center' }}>
              {/* Eyebrow pill */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{
                  pl: 0.75,
                  pr: 2,
                  py: 0.5,
                  borderRadius: 999,
                  background: '#ffffff',
                  border: '1px solid #e4e4e7',
                  boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)'
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#2563eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    fontSize: 12,
                    fontWeight: 700
                  }}
                >
                  ✶
                </Box>
                <Typography
                  sx={{
                    fontFamily: '"Roboto Mono", monospace',
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#0a0a0b'
                  }}
                >
                  Recent Work · {projects.length} projects
                </Typography>
              </Stack>

              {/* Headline */}
              <Typography
                component="h1"
                align="center"
                sx={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontSize: { xs: '44px', sm: '64px', md: '88px' },
                  fontWeight: 700,
                  lineHeight: 1.05,
                  letterSpacing: '-0.04em',
                  color: '#0a0a0b',
                  maxWidth: 1200,
                  px: { xs: 2, md: 0 }
                }}
              >
                Our recent work.
              </Typography>

              {/* Sub */}
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: 17, md: 19 },
                  fontWeight: 400,
                  lineHeight: 1.55,
                  color: '#52525b',
                  maxWidth: 680,
                  px: { xs: 2, md: 0 }
                }}
              >
                Cinematic commercials, brand films, websites, sports content, and full-scope production
                for ambitious Houston-area brands. Filter by category or browse the full reel.
              </Typography>
            </Stack>
          </Container>
        </Box>

        {/* Filter pills */}
        <Box sx={{ background: '#ffffff', pt: { xs: 4, md: 6 }, pb: { xs: 5, md: 7 } }}>
          <Container maxWidth="lg">
            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: 'center'
              }}
            >
              {FILTERS.map((f) => {
                const active = f === activeFilter;
                const count = f === 'All' ? projects.length : projects.filter((p) => p.category === f).length;
                return (
                  <Box
                    key={f}
                    onClick={() => setActiveFilter(f)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      borderRadius: 999,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      background: active ? '#0a0a0b' : '#ffffff',
                      border: '1px solid',
                      borderColor: active ? '#0a0a0b' : '#e4e4e7',
                      '&:hover': {
                        borderColor: active ? '#0a0a0b' : '#2563eb',
                        background: active ? '#0a0a0b' : 'rgba(37, 99, 235, 0.04)'
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: 14,
                        fontWeight: 500,
                        color: active ? '#ffffff' : '#0a0a0b'
                      }}
                    >
                      {f}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Roboto Mono", monospace',
                        fontSize: 11,
                        color: active ? 'rgba(255,255,255,0.6)' : '#a1a1aa'
                      }}
                    >
                      {count}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Container>
        </Box>

        {/* Project grid */}
        <Box sx={{ background: '#fafafa', py: { xs: 8, md: 12 } }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: { xs: 2.5, md: 3 }
              }}
            >
              {filteredProjects.map((p, i) => {
                const hasImage = !!p.image;
                const isYouTubeEmbed = !hasImage && typeof p.src === 'string' && p.src.includes('youtube.com/embed/');
                return (
                  <Box
                    key={`${activeFilter}-${i}`}
                    onClick={() => {
                      if (!p.src.startsWith('http')) return;
                      const ytMatch = p.src.match(/youtube\.com\/embed\/([^?/&]+)/);
                      if (ytMatch) {
                        const startMatch = p.src.match(/[?&]start=(\d+)/);
                        const watchUrl = `https://www.youtube.com/watch?v=${ytMatch[1]}${
                          startMatch ? `&t=${startMatch[1]}s` : ''
                        }`;
                        window.open(watchUrl, '_blank', 'noopener,noreferrer');
                        return;
                      }
                      window.open(p.src, '_blank', 'noopener,noreferrer');
                    }}
                    sx={{
                      position: 'relative',
                      borderRadius: 3,
                      overflow: 'hidden',
                      background: '#ffffff',
                      border: '1px solid #e4e4e7',
                      transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        borderColor: '#2563eb',
                        boxShadow: '0 32px 64px -20px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(37, 99, 235, 0.15)',
                        '& .media': { transform: 'scale(1.04)' }
                      }
                    }}
                  >
                    {/* Media */}
                    <Box
                      sx={{
                        position: 'relative',
                        aspectRatio: '16 / 10',
                        overflow: 'hidden',
                        background: '#0a0a0b'
                      }}
                    >
                      {hasImage ? (
                        <Box
                          className="media"
                          component="img"
                          src={p.image}
                          alt={p.title}
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'top center',
                            transition: 'transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)'
                          }}
                        />
                      ) : isYouTubeEmbed ? (
                        <Box
                          className="media"
                          component="iframe"
                          src={p.src}
                          title={p.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            border: 0
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            background: 'linear-gradient(135deg, #1e293b 0%, #0a0a0b 60%)'
                          }}
                        />
                      )}
                    </Box>

                    {/* Card body */}
                    <Stack spacing={1.5} sx={{ p: { xs: 2.5, md: 3 }, flex: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: { xs: 17, md: 19 },
                          fontWeight: 600,
                          letterSpacing: '-0.02em',
                          lineHeight: 1.25,
                          color: '#0a0a0b'
                        }}
                      >
                        {p.title}
                      </Typography>

                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        {p.tags.map((tag, idx) => (
                          <Box
                            key={tag}
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: 999,
                              background:
                                idx === 0 ? 'rgba(37, 99, 235, 0.08)' : 'rgba(168, 85, 247, 0.08)',
                              border: '1px solid',
                              borderColor:
                                idx === 0 ? 'rgba(37, 99, 235, 0.2)' : 'rgba(168, 85, 247, 0.2)'
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: '"Roboto Mono", monospace',
                                fontSize: 9.5,
                                fontWeight: 500,
                                letterSpacing: 0.6,
                                textTransform: 'uppercase',
                                color: idx === 0 ? '#1d4ed8' : '#7e22ce'
                              }}
                            >
                              {tag}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>

                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: 13.5,
                          fontWeight: 400,
                          lineHeight: 1.55,
                          color: '#52525b',
                          flex: 1
                        }}
                      >
                        {p.description}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
            </Box>

            {filteredProjects.length === 0 && (
              <Typography
                align="center"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#71717a',
                  py: 8
                }}
              >
                No projects in this category yet.
              </Typography>
            )}
          </Container>
        </Box>

        {/* Bottom CTA */}
        <Box sx={{ background: '#ffffff', py: { xs: 12, md: 16 } }}>
          <Container maxWidth="md">
            <Stack alignItems="center" spacing={{ xs: 3, md: 4 }} sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  color: '#52525b'
                }}
              >
                Ready to build yours?
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontSize: { xs: 36, md: 56 },
                  fontWeight: 700,
                  lineHeight: 1.08,
                  letterSpacing: '-0.03em',
                  color: '#0a0a0b'
                }}
              >
                Let’s make your next project.
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: 17, md: 19 },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: '#52525b',
                  maxWidth: 560
                }}
              >
                Tell us what you’re building and we’ll scope it in days, not weeks. Houston-based,
                veteran-owned, full media department under one roof.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ pt: 2 }}
              >
                <Button
                  component={RouterLink}
                  href={paths.contact}
                  variant="contained"
                  endIcon={
                    <SvgIcon sx={{ fontSize: 18 }}>
                      <ArrowRightIcon />
                    </SvgIcon>
                  }
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    borderRadius: 2,
                    background: '#2563eb',
                    color: '#ffffff',
                    textTransform: 'none',
                    boxShadow: '0 8px 24px -8px rgba(37, 99, 235, 0.45)',
                    '&:hover': {
                      background: '#1d4ed8',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Start a project
                </Button>
                <Button
                  component={RouterLink}
                  href={paths.about.index}
                  variant="outlined"
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    borderRadius: 2,
                    borderColor: '#e4e4e7',
                    borderWidth: 1.5,
                    color: '#0a0a0b',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#2563eb',
                      color: '#2563eb',
                      backgroundColor: 'rgba(37, 99, 235, 0.04)',
                      borderWidth: 1.5
                    }
                  }}
                >
                  Meet the team
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </main>
    </>
  );
};
